<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: "Inventory",
      text: "Inventory",
      search: '',
      data: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    text1() {
      return `Work Orders (${this.pagination.total})`;
    },
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    }
  },

  mounted() {
    this.loadWorkOrders();
  },

  methods: {

    async loadWorkOrders(page = 1) {
      this.setLoading(true);
      try {
        const params = {
          page,
          search: this.search.trim() || ''
        };
        const query_string = new URLSearchParams(params).toString();
        const response = await axios.get(`/work-orders?${query_string}`);
        this.data = response.data.data.data;
        this.pagination = {
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          links: response.data.data.links
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.loadWorkOrders(page);
    },

    clearFilter() {
      this.search = '';
      this.loadWorkOrders();
    },

    statusBadge(status) {
      const map = {
        active: 'bg-success-subtle text-success',
        inactive: 'bg-danger-subtle text-danger',
        completed: 'bg-info-subtle text-info',
        cancelled: 'bg-warning-subtle text-warning',
      };
      return map[status] || 'bg-secondary-subtle text-secondary';
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
    },

    async openViewWorkOrder(row) {
      await this.$refs.workOrderModal.viewWorkOrder(row);
      this.$openModal('work_order_modal');
    },

    async openEditWorkOrder(row) {
      await this.$refs.workOrderModal.openWorkOrder(row);
      this.$openModal('work_order_modal');
    },

    async openAddWorkOrder() {
      await this.$refs.workOrderModal.openWorkOrder();
      this.$openModal('work_order_modal');
    },

    async deleteWorkOrder(row) {
      try {
        const result = await this.$swal({
          title: 'Delete Work Order?',
          text: `Are you sure you want to delete "${row.name}"?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#e84040',
        });
        if (result.isConfirmed) {
          this.setLoading(true);
          const response = await axios.delete(`/work-orders/${row.id}`);
          this.$swalToast(response.data.message);
          this.loadWorkOrders(this.pagination.current_page);
        }
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="mb-2">
            <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center"
              @click="openAddWorkOrder()">
              <i class="ti ti-circle-plus me-2"></i>Add Work Order
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h5>Work Orders</h5>

          <div class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">

            <div class="me-3" v-if="search">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()">
                <i class="ti ti-filter-x"></i>
              </a>
            </div>

            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control" v-model="search" placeholder="Search...." />
              </div>
            </div>

            <div class="me-3">
              <button type="button" class="btn btn-dark" @click="loadWorkOrders()">
                <i class="ti ti-search"></i>
              </button>
            </div>

          </div>
        </div>

        <div class="card-body card-table-wrapper">
          <div class="custom-datatable-filter table-responsive">
            <table class="card-table" v-if="data.length">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Subtotal</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in data" :key="row.id">
                  <td>{{ serialNo + index + 1 }}.</td>
                  <td>
                    <a class="text-primary" href="javascript:void(0);" @click="openViewWorkOrder(row)">
                      {{ row.code }}
                    </a>
                  </td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.city }}</td>
                  <td>{{ row.state }}</td>
                  <td>{{ formatCurrency(row.subtotal) }}</td>
                  <td>{{ formatCurrency(row.tax_amount) }}</td>
                  <td>{{ formatCurrency(row.total) }}</td>
                  <td>
                    <span class="badge rounded-pill" :class="statusBadge(row.status)">
                      {{ row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '-' }}
                    </span>
                  </td>
                  <td>
                    <div class="dropdown">
                      <button class="btn btn-icon btn-sm rounded-circle" type="button"
                        data-bs-toggle="dropdown" aria-expanded="false">
                        <i class="ti ti-dots-vertical"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end p-3">
                        <li>
                          <a class="dropdown-item rounded-1" href="javascript:void(0);"
                            @click="openViewWorkOrder(row)">
                            <i class="ti ti-eye me-1"></i>View
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item rounded-1" href="javascript:void(0);"
                            @click="openEditWorkOrder(row)">
                            <i class="ti ti-edit me-1"></i>Edit
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);"
                            @click="deleteWorkOrder(row)">
                            <i class="ti ti-trash me-1"></i>Delete
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-else class="p-3 text-center text-muted">No records found.</div>
          </div>

          <!-- Pagination -->
          <nav v-if="pagination.links.length > 3" class="m-3">
            <ul class="pagination pagination-sm">
              <li v-for="(link, index) in pagination.links" :key="index"
                :class="['page-item', { active: link.active, disabled: !link.url }]">
                <a class="page-link" href="javascript:void(0)" v-html="link.label"
                  @click="changePage(link.url)"></a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <app-footer></app-footer>
  </div>
  <!-- /Page Wrapper -->

  <work-order-modal ref="workOrderModal" @updated="loadWorkOrders"></work-order-modal>

</template>

<style scoped>
.table-responsive {
  overflow-y: auto;
}
</style>